import { useState, useEffect } from "react";
import { Box, Slider, Typography, Paper, Button } from "@mui/material";
import axios from "axios";
import ImageIcon from "@mui/icons-material/Image";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ImageBrowserPopup from "../PreviousImagePopup/popup"; // Adjust the path as needed

export default function BlushApp() {
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [blushIntensity, setBlushIntensity] = useState(50); // 0-100
  const [blushRadius, setBlushRadius] = useState(80);
  const [blurFactor, setBlurFactor] = useState(151);
  const [blushColor, setBlushColor] = useState({ r: 255, g: 0, b: 0 });
  const [scanning, setScanning] = useState(false);
  const [imageBrowserOpen, setImageBrowserOpen] = useState(false);

  // Common button style for consistency
  const commonButtonStyle = {
    textTransform: "none",
    borderRadius: "8px",
    padding: "12px 20px",
    minWidth: "160px",
    fontSize: "16px",
    width: "200px",
  };

  // On component mount, check if a selected image exists in localStorage
  useEffect(
    () => {
      const storedImageUrl = localStorage.getItem("selectedImageUrl");
      if (storedImageUrl && !image) {
        handlePreviousImageSelect({ url: storedImageUrl });
      }
    }, // eslint-disable-next-line
    []
  );

  const handlePreviousImageSelect = async (selectedImage) => {
    setScanning(true);
    try {
      // Save the selected image URL in localStorage
      localStorage.setItem("selectedImageUrl", selectedImage.url);

      const response = await fetch(selectedImage.url);
      const blob = await response.blob();
      const file = new File([blob], "selected_image.jpg", {
        type: "image/jpeg",
      });
      setProcessedImage(URL.createObjectURL(file));
      setImage(file);
    } catch (error) {
      console.error("Error processing previous image:", error);
      toast.error("Error processing previous image.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setImageBrowserOpen(false);
    setScanning(false);
  };

  const processImage = async (file) => {
    setScanning(true);
    try {
      // Step 1: Upload the file to get an intermediate image
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_KEY}/makeup/upload`,
        formDataUpload,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        }
      );

      if (!uploadResponse.data) {
        throw new Error("Upload API did not return image data");
      }
      const intermediateImageUrl = URL.createObjectURL(uploadResponse.data);
      setProcessedImage(intermediateImageUrl);

      // Delay for the scanning animation (3 seconds)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Step 2: Apply blush using the API
      const formDataBlush = new FormData();
      formDataBlush.append("file", file);
      // Convert RGB to B,G,R order expected by the API
      formDataBlush.append(
        "blush_color",
        `${blushColor.b},${blushColor.g},${blushColor.r}`
      );
      formDataBlush.append(
        "blush_intensity",
        (blushIntensity / 100).toString()
      );
      formDataBlush.append("blush_radius", blushRadius.toString());
      formDataBlush.append("blur_factor", blurFactor.toString());

      const blushResponse = await axios.post(
        `${process.env.REACT_APP_API_KEY}/makeup/apply_blush`,
        formDataBlush,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        }
      );
      if (!blushResponse.data) {
        throw new Error("Blush API did not return image data");
      }
      const finalImageUrl = URL.createObjectURL(blushResponse.data);
      setProcessedImage(finalImageUrl);
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Error processing image. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setScanning(false);
  };

  // Click handler for the Apply button
  const handleApply = () => {
    if (image) {
      processImage(image);
    } else {
      toast.error("Please select or upload an image first.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 4,
        p: 4,
        pt: 16,
        pb: 16,
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#0d1117",
        fontFamily: "Roboto, sans-serif",
        color: "#fff",
      }}
    >
      {/* Image Browser Popup */}
      <ImageBrowserPopup
        open={imageBrowserOpen}
        onClose={() => setImageBrowserOpen(false)}
        onImageSelect={handlePreviousImageSelect}
      />

      {/* Processed Image Display */}
      <Paper
        elevation={6}
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 3,
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          height: { xs: "auto", md: "80vh" },
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {processedImage ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={processedImage}
              alt="Processed"
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100% - 80px)",
                borderRadius: "12px",
                transition: "0.3s ease",
                boxShadow: "0px 5px 20px rgba(255, 255, 255, 0.1)",
              }}
            />

            {/* Scanning Overlay */}
            {scanning && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "110%",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                {/* Horizontal scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: "3px",
                    background:
                      "linear-gradient(90deg, transparent, #EDB38F, #F98995, #A41847, transparent)",
                    boxShadow:
                      "0 0 10px #EDB38F, 0 0 20px #F98995, 0 0 30px #A41847",
                    animation: "scanHorizontal 2s ease-in-out infinite",
                    "@keyframes scanHorizontal": {
                      "0%": { top: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { top: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Vertical scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    height: "100%",
                    width: "3px",
                    background:
                      "linear-gradient(0deg, transparent, #EDB38F, #F98995, #A41847, transparent)",
                    boxShadow:
                      "0 0 10px #EDB38F, 0 0 20px #F98995, 0 0 30px #A41847",
                    animation: "scanVertical 2s ease-in-out infinite",
                    animationDelay: "1s",
                    "@keyframes scanVertical": {
                      "0%": { left: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { left: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Scanning overlay effect */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background:
                      "radial-gradient(circle, transparent, rgba(237, 179, 143, 0.05))",
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 0.1 },
                      "50%": { opacity: 0.3 },
                      "100%": { opacity: 0.1 },
                    },
                  }}
                />
              </Box>
            )}

            {/* Status Area: shows scanning text or Download button */}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              {scanning ? (
                <Typography
                  sx={{
                    color: "#F98995",
                    fontSize: { xs: 16, md: 18 },
                    fontWeight: "bold",
                  }}
                >
                  Beautifying in progress...
                  <br />
                  Getting your glow-up started!
                </Typography>
              ) : (
                <a href={processedImage} download="processed_blush_image.png">
                  <Button
                    variant="contained"
                    sx={{
                      ...commonButtonStyle,
                      background: "#a41847",
                      "&:hover": { background: "#860f37" },
                    }}
                  >
                    Download Image
                  </Button>
                </a>
              )}
            </Box>
          </Box>
        ) : (
          <Typography sx={{ color: "#ccc", fontSize: 18, textAlign: "center" }}>
            {scanning ? "Scanning image..." : "Upload an image to apply blush"}
          </Typography>
        )}
      </Paper>

      {/* Controls */}
      <Paper
        elevation={6}
        sx={{
          p: 4,
          borderRadius: 3,
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          height: { xs: "auto", md: "auto" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "uploaded");
            }}
          >
            Uploaded Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "captured");
            }}
          >
            Captured Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "model");
            }}
          >
            Model Image
          </Button>
        </Box>

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Blush Intensity
        </Typography>
        <Slider
          value={blushIntensity}
          min={0}
          max={100}
          onChange={(e, newValue) => setBlushIntensity(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Blush Radius
        </Typography>
        <Slider
          value={blushRadius}
          min={10}
          max={200}
          onChange={(e, newValue) => setBlushRadius(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Blur Factor
        </Typography>
        <Slider
          value={blurFactor}
          min={0}
          max={300}
          onChange={(e, newValue) => setBlurFactor(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Blush Color
        </Typography>
        <input
          type="color"
          value={`#${blushColor.r.toString(16).padStart(2, "0")}${blushColor.g
            .toString(16)
            .padStart(2, "0")}${blushColor.b.toString(16).padStart(2, "0")}`}
          onChange={(e) => {
            const hex = e.target.value;
            setBlushColor({
              r: parseInt(hex.slice(1, 3), 16),
              g: parseInt(hex.slice(3, 5), 16),
              b: parseInt(hex.slice(5, 7), 16),
            });
          }}
          style={{
            width: "100%",
            height: 50,
            border: "none",
            cursor: "pointer",
            borderRadius: "6px",
            background: "none",
            boxShadow: "0px 3px 12px rgba(255, 255, 255, 0.1)",
          }}
        />

        {/* Apply Button */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={scanning}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
              width: { xs: "100%", md: "auto" },
              mx: "auto", // centers horizontally if width < 100%
              display: "block",
            }}
          >
            Apply Makeup
          </Button>
        </Box>
      </Paper>

      {/* Toast container for notifications */}
      <ToastContainer />
    </Box>
  );
}
